import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';

import { TenantContext } from '#/app/shared/services/tenant.context';

import type {
  CreatePlanProjectAndAssignAssetsRequest,
  CreatePlanProjectRequest,
  MovePlanProjectSortOrderResponse,
  PlanProjectResponse,
  PlanProjectYearGroupResponse,
  SortOrderDirection,
  UpdatePlanProjectRequest,
} from '../../domain/plans/plan-project.model';
import { assertIsDefined } from '../../domain/types';
import { DataInvalidationService } from '../data-invalidation.service';

@Injectable({ providedIn: 'root' })
export class PlanProjectsService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  planProjectsByYearResource(planId: Signal<string>) {
    return httpResource<PlanProjectYearGroupResponse[]>(() => {
      this.#dataInvalidationService.track('plans:projects');
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();

      return tenantId && planIdValue
        ? `/api/tenants/${tenantId}/plans/${planIdValue}/projects/years`
        : undefined;
    });
  }

  planProjectResource(planId: Signal<string>, planProjectId: Signal<string>) {
    return httpResource<PlanProjectResponse>(() => {
      this.#dataInvalidationService.track([
        'plans:projects',
        'plans:projects:assets',
      ]);
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();
      const planProjectIdValue = planProjectId();
      return tenantId && planIdValue && planProjectIdValue
        ? `/api/tenants/${tenantId}/plans/${planIdValue}/projects/${planProjectIdValue}`
        : undefined;
    });
  }

  createPlanProject(
    planId: string,
    request: CreatePlanProjectRequest,
  ): Observable<PlanProjectResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<PlanProjectResponse>(
      `/api/tenants/${tenantId}/plans/${planId}/projects`,
      request,
    );
  }

  updatePlanProject(
    planId: string,
    planProjectId: string,
    request: UpdatePlanProjectRequest,
  ): Observable<PlanProjectResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.put<PlanProjectResponse>(
      `/api/tenants/${tenantId}/plans/${planId}/projects/${planProjectId}`,
      request,
    );
  }

  createPlanProjectAndAssignAssets(
    planId: string,
    request: CreatePlanProjectAndAssignAssetsRequest,
  ): Observable<PlanProjectResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<PlanProjectResponse>(
      `/api/tenants/${tenantId}/plans/${planId}/projects/assets`,
      request,
    );
  }

  movePlanProjectOrder(
    planId: string,
    planProjectId: string,
    direction: SortOrderDirection,
  ): Observable<MovePlanProjectSortOrderResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.put<MovePlanProjectSortOrderResponse>(
      `/api/tenants/${tenantId}/plans/${planId}/projects/${planProjectId}/move/${direction as string}`,
      null,
    );
  }
}
